/**
 * A child's animal, drawn ([Avatar](../../../docs/AVATAR.md),
 * [D-158](../../../docs/DECISIONS.md#d-158)).
 *
 * One component for every place a child sees themselves — sign-in, the grown-up
 * list, the profile, the picker — so a child who picks the axolotl is the
 * axolotl everywhere, not an axolotl on one screen and a fox on the next.
 *
 * **The markup is local.** It comes from the generated bodies in
 * `@capytalk/tokens`, committed and checked in CI; the server only names a
 * species, and a name the catalogue does not have draws a paw print instead.
 */

import { avatarBody, avatarSvg } from '@capytalk/tokens';

type Worn = Parameters<typeof avatarSvg>[0]['loadout'];

export default function Animal({
  species,
  background,
  width,
  uid,
  worn,
  label,
}: {
  readonly species: string;
  readonly background: string;
  readonly width: number;
  /** Unique on the page: two avatars sharing one would share gradients. */
  readonly uid: string;
  readonly worn?: Worn;
  /** Given when the animal is the thing on screen; decorative otherwise. */
  readonly label?: string;
}) {
  const height = (width * 240) / 160;
  if (avatarBody(species) === undefined) {
    return (
      <span
        style={{ fontSize: width / 2, lineHeight: 1 }}
        {...(label === undefined
          ? { 'aria-hidden': true }
          : { role: 'img', 'aria-label': label })}
      >
        🐾
      </span>
    );
  }
  return (
    <svg
      viewBox="0 0 160 240"
      width={width}
      height={height}
      {...(label === undefined
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': label })}
      // Local, generated, CI-checked markup keyed by a species id — never
      // anything a response chose. See the note at the top of this file.
      dangerouslySetInnerHTML={{
        __html: avatarSvg({ species, background, loadout: worn, uid }),
      }}
    />
  );
}
